import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SubscriptionTierIndicator from '../../components/ui/SubscriptionTierIndicator';
import SearchForm from './components/SearchForm';
import SearchResults from './components/SearchResults';
import SavedSearches from './components/SavedSearches';
import QuickStats from './components/QuickStats';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CraneSearchDashboard = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Mock crane data
  const mockCranes = [
  {
    id: 1,
    make: "Liebherr",
    model: "LTM 1130-5.1",
    year: 2019,
    hours: 2847,
    capacity: 130,
    boomLength: 60,
    condition: 4,
    location: "Houston, TX",
    dealer: "Texas Crane & Equipment",
    dealerLocation: "Houston, TX",
    daysOnMarket: 23,
    priceRange: {
      min: 850000,
      max: 950000,
      avg: 900000
    },
    confidenceScore: 87,
    image: "https://images.unsplash.com/photo-1733067996013-ec5b8eec3f52",
    imageAlt: "Yellow Liebherr mobile crane with extended boom at construction site",
    isNew: true
  },
  {
    id: 2,
    make: "Grove",
    model: "GMK5150L",
    year: 2020,
    hours: 1523,
    capacity: 150,
    boomLength: 60,
    condition: 5,
    location: "Atlanta, GA",
    dealer: "Southeast Heavy Equipment",
    dealerLocation: "Atlanta, GA",
    daysOnMarket: 45,
    priceRange: {
      min: 1200000,
      max: 1350000,
      avg: 1275000
    },
    confidenceScore: 92,
    image: "https://images.unsplash.com/photo-1580205397275-76b5e8da1686",
    imageAlt: "Green Grove mobile crane operating at industrial construction site",
    isNew: false
  },
  {
    id: 3,
    make: "Terex",
    model: "AC 100/4L",
    year: 2018,
    hours: 4250,
    capacity: 100,
    boomLength: 50,
    condition: 3,
    location: "Phoenix, AZ",
    dealer: "Desert Crane Sales",
    dealerLocation: "Phoenix, AZ",
    daysOnMarket: 78,
    priceRange: {
      min: 650000,
      max: 750000,
      avg: 700000
    },
    confidenceScore: 78,
    image: "https://images.unsplash.com/photo-1536547789702-ad9971b6c40b",
    imageAlt: "Red Terex all-terrain crane with telescopic boom at construction yard",
    isNew: false
  },
  {
    id: 4,
    make: "Manitowoc",
    model: "MLC300",
    year: 2021,
    hours: 890,
    capacity: 300,
    boomLength: 80,
    condition: 5,
    location: "Chicago, IL",
    dealer: "Midwest Crane Solutions",
    dealerLocation: "Chicago, IL",
    daysOnMarket: 12,
    priceRange: {
      min: 2800000,
      max: 3200000,
      avg: 3000000
    },
    confidenceScore: 95,
    image: "https://images.unsplash.com/photo-1678860886480-d0d2a3757597",
    imageAlt: "Large red Manitowoc crawler crane with lattice boom at industrial site",
    isNew: true
  },
  {
    id: 5,
    make: "Tadano",
    model: "GR-1000XL",
    year: 2017,
    hours: 5670,
    capacity: 100,
    boomLength: 51,
    condition: 3,
    location: "Denver, CO",
    dealer: "Rocky Mountain Equipment",
    dealerLocation: "Denver, CO",
    daysOnMarket: 156,
    priceRange: {
      min: 580000,
      max: 680000,
      avg: 630000
    },
    confidenceScore: 72,
    image: "https://images.unsplash.com/photo-1663046064203-7f38d178afe2",
    imageAlt: "White Tadano rough terrain crane with telescopic boom in equipment yard",
    isNew: false
  },
  {
    id: 6,
    make: "Link-Belt",
    model: "RTC-8090",
    year: 2022,
    hours: 456,
    capacity: 90,
    boomLength: 47,
    condition: 5,
    location: "Miami, FL",
    dealer: "Florida Crane & Rigging",
    dealerLocation: "Miami, FL",
    daysOnMarket: 8,
    priceRange: {
      min: 950000,
      max: 1050000,
      avg: 1000000
    },
    confidenceScore: 89,
    image: "https://images.unsplash.com/photo-1628166562558-0397b844bc01",
    imageAlt: "Blue Link-Belt rough terrain crane with extended outriggers at port facility",
    isNew: true
  }];


  const breadcrumbItems = [
  { label: 'Dashboard', path: '/crane-search-dashboard' },
  { label: 'Crane Search', path: null }];


  const handleSearch = async (searchParams) => {
    setIsLoading(true);
    setCurrentPage(1);

    // Simulate API call
    setTimeout(() => {
      // Filter mock data based on search params
      let filteredResults = mockCranes;

      if (searchParams?.make) {
        filteredResults = filteredResults?.filter((crane) =>
        crane?.make?.toLowerCase() === searchParams?.make?.toLowerCase()
        );
      }

      if (searchParams?.yearFrom) {
        filteredResults = filteredResults?.filter((crane) =>
        crane?.year >= parseInt(searchParams?.yearFrom)
        );
      }

      if (searchParams?.yearTo) {
        filteredResults = filteredResults?.filter((crane) =>
        crane?.year <= parseInt(searchParams?.yearTo)
        );
      }

      setSearchResults(filteredResults);
      setTotalCount(filteredResults?.length);
      setHasMore(filteredResults?.length > 6);
      setIsLoading(false);
    }, 1500);
  };

  const handleGetValuation = (crane) => {
    console.log('Getting valuation for:', crane);
    window.location.href = `/crane-valuation-report?id=${crane?.id}`;
  };

  const handleLoadMore = () => {
    setIsLoading(true);
    setCurrentPage((prev) => prev + 1);

    // Simulate loading more results
    setTimeout(() => {
      setIsLoading(false);
      setHasMore(false);
    }, 1000);
  };

  // Load initial data
  useEffect(() => {
    handleSearch({});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar />
      
      <main className="container mx-auto px-6 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="space-y-2">
            <Breadcrumbs items={breadcrumbItems} />
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Search" size={24} color="white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Crane Search Dashboard</h1>
                <p className="text-text-secondary">Find and value cranes with comprehensive market data</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <SubscriptionTierIndicator tier="pro" />
            <Button
              variant="outline"
              onClick={() => window.location.href = '/inventory-management'}
              iconName="Package"
              iconPosition="left">

              Manage Inventory
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStats />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Search and Results - Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* Search Form */}
            <SearchForm
              onSearch={handleSearch}
              isLoading={isLoading} />


            {/* Search Results */}
            <SearchResults
              results={searchResults}
              isLoading={isLoading}
              totalCount={totalCount}
              onGetValuation={handleGetValuation}
              onLoadMore={handleLoadMore}
              hasMore={hasMore} />

          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 space-y-6">
            {/* Saved Searches */}
            <SavedSearches />

            {/* Quick Actions */}
            <div className="bg-surface border border-border rounded-lg shadow-card p-4">
              <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
                <Icon name="Zap" size={16} className="text-accent" />
                <span>Quick Actions</span>
              </h3>
              
              <div className="space-y-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/comparison-tool'}
                  iconName="GitCompare"
                  iconPosition="left"
                  fullWidth>

                  Compare Cranes
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.href = '/portfolio-dashboard'}
                  iconName="BarChart3"
                  iconPosition="left"
                  fullWidth>

                  Portfolio Analysis
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => console.log('Export search results')}
                  iconName="Download"
                  iconPosition="left"
                  fullWidth>

                  Export Results
                </Button>
              </div>
            </div>

            {/* Market Insights */}
            <div className="bg-surface border border-border rounded-lg shadow-card p-4">
              <h3 className="font-medium text-text-primary mb-4 flex items-center space-x-2">
                <Icon name="TrendingUp" size={16} className="text-success" />
                <span>Market Insights</span>
              </h3>
              
              <div className="space-y-4">
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-text-primary">Mobile Cranes</span>
                    <span className="text-sm text-success">+3.2%</span>
                  </div>
                  <p className="text-xs text-text-secondary">Strong demand in construction sector</p>
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-text-primary">RT Cranes</span>
                    <span className="text-sm text-warning">-1.1%</span>
                  </div>
                  <p className="text-xs text-text-secondary">Seasonal adjustment in pricing</p>
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-text-primary">Crawler Cranes</span>
                    <span className="text-sm text-success">+5.8%</span>
                  </div>
                  <p className="text-xs text-text-secondary">Infrastructure projects driving growth</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default CraneSearchDashboard;